import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import { JobsAndRatesPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import JobsAndRatesDataCollection from 'modules/project_management/components/tables/JobsAndRatesDataCollection'

const JobsAndRatesPage = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openAddSidepanel, setOpenAddSidepanel] = useState(false)

  const { data, loading, error } = useQuery(JobsAndRatesPageDocument, {
    variables: { companyId, featureIds: [Features.projects_budget, Features.projects_costs] },
  })

  const isTotalProfitabilityEnabled = !!data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_projects_total_profitability'
  )?.enabled

  const isBudgetFeatureEnabled = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_budget
  )?.available

  const isCostsFeatureEnabled = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available

  const canManageRates = !!data?.projectManagement._policies.rateManage.can
  const canSeeLaborCost = !!data?.projectManagement._policies.projectSeeLaborCost.can
  const canSeeRates = isBudgetFeatureEnabled && isCostsFeatureEnabled && canSeeLaborCost

  useEffect(() => {
    const openSidepanel = searchParams.get('openAddSidepanel')
    if (openSidepanel === 'true') {
      setOpenAddSidepanel(true)
      // Remove the parameter from URL after opening sidepanel
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('openAddSidepanel')
        return newParams
      })
    }
  }, [searchParams, setSearchParams])

  return (
    <ResponsivePage.Body
      variant="wide"
      loading={!data && loading}
      error={error}
      authorized={isTotalProfitabilityEnabled && canSeeRates}
    >
      <SectionHeader
        title={i18n.t('project_management.jobs_and_rates.header.title')}
        description={i18n.t('project_management.jobs_and_rates.header.description')}
        link={{
          href: `https://help.factorialhr.com/${i18n.t(
            'project_management.projects.lower_case_title'
          )}`,
          label: i18n.t('project_management.jobs_and_rates.header.secondary_button'),
        }}
        action={{
          icon: ExternalLink,
          label: i18n.t('project_management.jobs_and_rates.header.main_button'),
          onClick: () => {
            navigate(navigation.employees.jobCatalog.overview.fullPath())
          },
        }}
      />
      <JobsAndRatesDataCollection
        canManageRates={canManageRates}
        openAddSidepanel={openAddSidepanel}
        setOpenAddSidepanel={setOpenAddSidepanel}
      />
    </ResponsivePage.Body>
  )
}

export default JobsAndRatesPage
